import { $isListItemNode } from '@lexical/list';
import { Document, Packer, Paragraph, SectionType, TextRun } from 'docx';
import { $isLineBreakNode, $isParagraphNode, type LexicalNode } from 'lexical';

import { $traverseNodes } from './teoLexicalUtility';
import { $isTeoTextNode, TeoTextNode } from './TeoTextNode';

export function $docxExport(node: LexicalNode, filename: string): void {
  const paragraphs: Paragraph[] = [];
  let currentParagraph: Paragraph | null = null;
  let currentParagraphHasContent = false;
  let runningText = '';
  let currentFormatId: number | null = null;
  let currentFormat = {
    bold: false,
    italic: false,
    underline: false,
  };

  function flushRunningText() {
    if (!currentParagraph || runningText === '') return;
    currentParagraph.addChildElement(docxCreateTextRun(runningText, currentFormat));
    currentParagraphHasContent = true;
    runningText = '';
  }

  function finalizeParagraph() {
    flushRunningText();
    if (currentParagraph && currentParagraphHasContent) {
      paragraphs.push(currentParagraph);
    }
    currentParagraph = null;
    currentParagraphHasContent = false;
    currentFormatId = null;
  }

  function startParagraph(opts: ConstructorParameters<typeof Paragraph>[0]) {
    finalizeParagraph();
    currentParagraph = new Paragraph(opts);
  }

  $traverseNodes(node, (node: LexicalNode) => {
    if ($isParagraphNode(node)) {
      startParagraph({});
      return;
    }

    if ($isListItemNode(node)) {
      startParagraph({
        bullet: {
          level: 0,
        },
      });
      return;
    }

    if ($isLineBreakNode(node)) {
      if (!currentParagraph) startParagraph({});
      flushRunningText();
      currentParagraph!.addChildElement(docxCreateLineBreakRun());
      currentParagraphHasContent = true;
      currentFormatId = null;
      return;
    }

    if ($isTeoTextNode(node)) {
      if (!currentParagraph) startParagraph({});
      if (currentFormatId === null) {
        currentFormatId = node.getFormat();
        currentFormat = getFormatObj(node);
      } else if (runningText !== '' && currentFormatId !== node.getFormat()) {
        flushRunningText();
        currentFormatId = node.getFormat();
        currentFormat = getFormatObj(node);
      }
      runningText += node.getTextContent();
    }
  });
  finalizeParagraph();
  const doc = new Document({
    sections: [
      {
        properties: {
          type: SectionType.CONTINUOUS,
        },
        children: paragraphs,
      },
    ],
  });
  Packer.toBlob(doc).then((blob) => downloadFile(blob, filename + '.docx'));
}

function getFormatObj(node: TeoTextNode): { bold: boolean; italic: boolean; underline: boolean } {
  return {
    bold: node.hasFormat('bold'),
    italic: node.hasFormat('italic'),
    underline: node.hasFormat('underline'),
  };
}

function docxCreateTextRun(
  text: string,
  format: { bold: boolean; italic: boolean; underline: boolean },
): TextRun {
  return new TextRun({
    text,
    bold: format.bold,
    italics: format.italic,
    underline: format.underline ? {} : undefined,
  });
}

function docxCreateLineBreakRun(): TextRun {
  return new TextRun({
    break: 1,
  });
}

function downloadFile(csvData: Blob | string, filename: string) {
  const url = window.URL.createObjectURL(csvData instanceof Blob ? csvData : new Blob([csvData]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();

  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
}
